import React, { useEffect, useState, Component } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MenuIcon, XIcon, ChevronDownIcon, SearchIcon, PhoneIcon, MailIcon, CalendarIcon } from 'lucide-react';
import { scrollToSection } from '../../utils/scrollToSection';
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);
  // Detect scroll for sticky header styling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close dropdown when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);
  // Handle hash navigation when page loads
  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const elementId = location.hash.substring(1);
        scrollToSection(elementId);
      }, 300);
    }
  }, [location]);
  const toggleDropdown = (menu: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (openDropdown === menu) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(menu);
    }
  };
  const handleLinkClick = (path: string, e: React.MouseEvent) => {
    const [pagePath, hash] = path.split('#');
    if (hash) {
      if (location.pathname === pagePath || !pagePath && location.pathname === '/') {
        e.preventDefault();
        scrollToSection(hash);
        window.history.pushState(null, '', path);
      }
    }
  };
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setSearchOpen(false);
    }
  };
  const menuItems = [{
    name: 'About',
    path: '/about',
    dropdown: [{
      name: 'History & Founding',
      path: '/about#history'
    }, {
      name: 'Vision & Mission',
      path: '/about#vision'
    }, {
      name: 'School Anthem',
      path: '/about#anthem'
    }, {
      name: 'School Badge & Motto',
      path: '/about#badge'
    }, {
      name: 'Leadership Team',
      path: '/about#leadership'
    }, {
      name: 'Isonyin Community',
      path: '/isonyin-community'
    }, {
      name: 'Emmanuel Church',
      path: '/emmanuel-church'
    }, {
      name: 'Islamic Religion',
      path: '/islamic-religion'
    }]
  }, {
    name: '70th Anniversary',
    path: '/anniversary',
    highlight: true,
    dropdown: [{
      name: 'Anniversary Overview',
      path: '/anniversary#overview'
    }, {
      name: 'Celebration Programme',
      path: '/anniversary#programme'
    }, {
      name: 'Alumni Registration',
      path: '/anniversary#register'
    }, {
      name: 'Support & Donations',
      path: '/anniversary#support'
    }, {
      name: 'Anniversary Gallery',
      path: '/anniversary-gallery'
    }, {
      name: 'Anniversary News',
      path: '/anniversary-news'
    }, {
      name: 'Distinguished Alumni',
      path: '/anniversary#alumni'
    }, {
      name: '📺 Live Stream',
      path: '/anniversary#livestream'
    }]
  }, {
    name: 'Campus',
    path: '/campus',
    dropdown: [{
      name: 'Virtual Tour',
      path: '/campus#tour'
    }, {
      name: 'Facilities',
      path: '/campus#facilities'
    }]
  }, {
    name: 'Academics',
    path: '/academics',
    dropdown: [{
      name: 'Curriculum',
      path: '/academics#curriculum'
    }, {
      name: 'Performance Records',
      path: '/academics#performance'
    }, {
      name: 'Teaching Staff',
      path: '/academics#staff'
    }, {
      name: 'Academic Calendar',
      path: '/academics#calendar'
    }, {
      name: 'Clubs & Societies',
      path: '/academics#clubs'
    }]
  }, {
    name: 'Admissions',
    path: '/admissions',
    dropdown: [{
      name: 'How to Apply',
      path: '/admissions#apply'
    }, {
      name: 'Requirements',
      path: '/admissions#requirements'
    }, {
      name: 'Forms',
      path: '/admissions#forms'
    }, {
      name: 'Fees',
      path: '/admissions#fees'
    }, {
      name: 'FAQs',
      path: '/admissions#faqs'
    }]
  }, {
    name: 'Student Life',
    path: '/student-life',
    dropdown: [{
      name: 'Boarding House',
      path: '/student-life#boarding'
    }, {
      name: 'Sports',
      path: '/student-life#sports'
    }, {
      name: 'Events',
      path: '/student-life#events'
    }, {
      name: 'Testimonials',
      path: '/student-life#testimonials'
    }]
  }, {
    name: 'News',
    path: '/news'
  }, {
    name: 'Events',
    path: '/events'
  }, {
    name: 'Gallery',
    path: '/gallery'
  }, {
    name: 'PTA',
    path: '/pta'
  }, {
    name: 'Alumni',
    path: '/alumni',
    dropdown: [{
      name: 'Alumni Association',
      path: '/alumni#association'
    }, {
      name: 'Executive Committee',
      path: '/alumni/excos'
    }, {
      name: 'Sets Directory',
      path: '/alumni#directory'
    }, {
      name: 'Featured Alumni',
      path: '/alumni#featured'
    }, {
      name: 'Business Directory',
      path: '/alumni#business'
    }]
  }, {
    name: 'Scholarships',
    path: '/scholarships'
  }, {
    name: 'Support Us',
    path: '/support'
  }, {
    name: 'Contact',
    path: '/contact'
  }];
  return <>
      {/* Top bar with contact info */}
      <div className="bg-blue-950 text-white py-2 hidden md:block" data-id="element-463">
        <div className="container mx-auto px-4" data-id="element-464">
          <div className="flex justify-between items-center text-sm" data-id="element-465">
            <div className="flex items-center space-x-4" data-id="element-466">
              <a href="tel:+2349055172703" className="flex items-center hover:text-yellow-300 transition-colors" data-id="element-467">
                <PhoneIcon className="h-3 w-3 mr-1" data-id="element-468" />
                <span data-id="element-469">+234 905 517 2703</span>
              </a>
              <a href="mailto:info@isonyingrammarschool.ng" className="flex items-center hover:text-yellow-300 transition-colors" data-id="element-470">
                <MailIcon className="h-3 w-3 mr-1" data-id="element-471" />
                <span data-id="element-472">info@isonyingrammarschool.ng</span>
              </a>
            </div>
            <div className="flex items-center space-x-4" data-id="element-473">
              <a href="/events" className="flex items-center hover:text-yellow-300 transition-colors" data-id="element-474">
                <CalendarIcon className="h-3 w-3 mr-1" data-id="element-475" />
                <span data-id="element-476">Academic Calendar</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main header */}
      <header className={`bg-blue-900 text-white sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'shadow-lg py-2' : 'py-4'}`} data-id="element-477">
        <div className="container mx-auto px-4" data-id="element-478">
          <div className="flex justify-between items-center" data-id="element-479">
            <Link to="/" className="flex items-center space-x-3 group" data-id="element-480">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/sVQyanUwhMphyB8je7Yk6r/4.png" alt="Isonyin Grammar School Logo" className={`transition-all duration-300 ${isScrolled ? 'h-12 w-12' : 'h-14 w-14'} object-contain transform group-hover:scale-110`} data-id="element-481" />
              <div className="hidden sm:block" data-id="element-482">
                <h1 className={`font-bold leading-tight transition-all duration-300 ${isScrolled ? 'text-lg' : 'text-xl'}`} data-id="element-483">
                  Isonyin Grammar School
                </h1>
                <p className="text-xs text-yellow-300" data-id="element-484">God is our Foundation</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1" data-id="element-485">
              {menuItems.slice(0, 7).map(item => <div key={item.name} className="relative group" data-id="element-486">
                  {item.dropdown ? <button className={`px-3 py-2 rounded-md flex items-center text-sm font-medium transition-colors ${location.pathname === item.path ? 'bg-blue-800 text-white' : item.highlight ? 'text-yellow-400 hover:bg-blue-800 hover:text-yellow-300' : 'hover:bg-blue-800 hover:text-white'}`} onClick={e => toggleDropdown(item.name, e)} data-id="element-487">
                      <span data-id="element-488">{item.name}</span>
                      <ChevronDownIcon className={`ml-1 h-4 w-4 transition-transform ${openDropdown === item.name ? 'rotate-180' : ''}`} data-id="element-489" />
                    </button> : <Link to={item.path} className={`px-3 py-2 rounded-md flex items-center text-sm font-medium transition-colors ${location.pathname === item.path ? 'bg-blue-800 text-white' : item.highlight ? 'text-yellow-400 hover:bg-blue-800 hover:text-yellow-300' : 'hover:bg-blue-800 hover:text-white'}`} onClick={e => handleLinkClick(item.path, e)} data-id="element-490">
                      {item.name}
                    </Link>}
                  {item.dropdown && openDropdown === item.name && <div className="absolute left-0 mt-1 w-56 bg-white rounded-md shadow-lg z-10 overflow-hidden animate-fadeIn" data-id="element-491">
                      <div className="py-1" data-id="element-492">
                        {item.dropdown.map(subItem => <Link key={subItem.name} to={subItem.path} className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-900 transition-colors" onClick={e => {
                    setOpenDropdown(null);
                    handleLinkClick(subItem.path, e);
                  }} data-id="element-493">
                            {subItem.name}
                          </Link>)}
                      </div>
                    </div>}
                </div>)}
              <div className="relative group" data-id="element-494">
                <button className="px-3 py-2 hover:bg-blue-800 rounded-md flex items-center text-sm font-medium transition-colors" onClick={e => toggleDropdown('More', e)} data-id="element-495">
                  More
                  <ChevronDownIcon className={`ml-1 h-4 w-4 transition-transform ${openDropdown === 'More' ? 'rotate-180' : ''}`} data-id="element-496" />
                </button>
                {openDropdown === 'More' && <div className="absolute right-0 mt-1 w-56 bg-white rounded-md shadow-lg z-10 overflow-hidden animate-fadeIn" data-id="element-497">
                    <div className="py-1" data-id="element-498">
                      {menuItems.slice(7).map(item => <Link key={item.name} to={item.path} className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-900 transition-colors" onClick={e => {
                    setOpenDropdown(null);
                    handleLinkClick(item.path, e);
                  }} data-id="element-499">
                          {item.name}
                        </Link>)}
                    </div>
                  </div>}
              </div>
              <button className="p-2 hover:bg-blue-800 rounded-full transition-colors" onClick={() => setSearchOpen(!searchOpen)} aria-label="Search" data-id="element-500">
                <SearchIcon className="h-5 w-5" data-id="element-501" />
              </button>
              <Link to="/admissions" className="ml-2 px-4 py-2 bg-yellow-500 text-blue-900 font-bold rounded-md hover:bg-yellow-400 transition-colors shadow-md hover:shadow-lg transform hover:-translate-y-0.5" data-id="element-502">
                Apply Now
              </Link>
            </nav>

            {/* Mobile menu button */}
            <div className="lg:hidden flex items-center space-x-2" data-id="element-503">
              <button className="p-2 hover:bg-blue-800 rounded-full transition-colors" onClick={() => setSearchOpen(!searchOpen)} aria-label="Search" data-id="element-504">
                <SearchIcon className="h-5 w-5" data-id="element-505" />
              </button>
              <button className="p-2 rounded-md hover:bg-blue-800 transition-colors" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label={isMenuOpen ? 'Close menu' : 'Open menu'} data-id="element-506">
                {isMenuOpen ? <XIcon className="h-6 w-6" data-id="element-507" /> : <MenuIcon className="h-6 w-6" data-id="element-508" />}
              </button>
            </div>
          </div>

          {/* Search bar */}
          {searchOpen && <div className="py-4 animate-fadeIn" data-id="element-509">
              <form onSubmit={handleSearch} className="relative" data-id="element-510">
                <input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full px-4 py-2 rounded-md bg-blue-800 border border-blue-700 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-yellow-500" autoFocus data-id="element-511" />
                <button type="submit" className="absolute right-2 top-1/2 transform -translate-y-1/2 text-blue-300 hover:text-white" data-id="element-512">
                  <SearchIcon className="h-5 w-5" data-id="element-513" />
                </button>
              </form>
            </div>}
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && <div className="lg:hidden py-4 border-t border-blue-800 animate-fadeIn" data-id="element-514">
            <div className="container mx-auto px-4" data-id="element-515">
              <div className="flex flex-col space-y-2" data-id="element-516">
                {menuItems.map(item => <div key={item.name} data-id="element-517">
                    <div className={`flex justify-between items-center px-3 py-2 hover:bg-blue-800 rounded-md transition-colors ${item.highlight ? 'text-yellow-400' : ''}`} onClick={e => item.dropdown && toggleDropdown(item.name, e)} data-id="element-518">
                      <Link to={item.path} onClick={e => {
                  if (!item.dropdown) {
                    setIsMenuOpen(false);
                    handleLinkClick(item.path, e);
                  }
                }} className={location.pathname === item.path ? 'font-bold' : ''} data-id="element-519">
                        {item.name}
                      </Link>
                      {item.dropdown && <ChevronDownIcon className={`h-4 w-4 transition-transform ${openDropdown === item.name ? 'rotate-180' : ''}`} data-id="element-520" />}
                    </div>
                    {item.dropdown && openDropdown === item.name && <div className="ml-4 mt-1 space-y-1 border-l-2 border-blue-700 pl-2 animate-fadeIn" data-id="element-521">
                        {item.dropdown.map(subItem => <Link key={subItem.name} to={subItem.path} className="block px-3 py-1 text-sm hover:bg-blue-800 rounded-md transition-colors" onClick={e => {
                  setOpenDropdown(null);
                  setIsMenuOpen(false);
                  handleLinkClick(subItem.path, e);
                }} data-id="element-522">
                            {subItem.name}
                          </Link>)}
                      </div>}
                  </div>)}
                <Link to="/admissions" className="mt-4 px-3 py-2 bg-yellow-500 text-blue-900 font-bold rounded-md hover:bg-yellow-400 transition-colors text-center shadow-md" onClick={() => setIsMenuOpen(false)} data-id="element-523">
                  Apply Now
                </Link>
              </div>
            </div>
          </div>}
      </header>
    </>;
};
export default Header;